import { useState, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import { trackEvent } from '../utils/posthog';
import SearchFilterPanel, { FilterState } from '../components/SearchFilterPanel';
import { getAuthToken } from '../utils/authToken';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import MerchantLogo from '../components/MerchantLogo';

interface Receipt {
  receipt_id: string;
  merchant: string;
  datetime: string;
  total_gross: number;
  currency: string;
  source: string;
  status: string;
  has_split: boolean;
  created_at: string;
  user_share_amount: number | null;
  logo?: {
    hasLogo: boolean;
    location: 'bundled' | 'blob' | null;
    filename: string | null;
    fallbackColor: string;
  };
}

interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export default function BlockyPage() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    datePreset: null,
    dateFrom: null,
    dateTo: null,
    amountPreset: null,
    amountMin: '',
    amountMax: '',
    splitStatus: 'all',
    selectedMerchants: [],
    merchantSearch: '',
    selectedCategory: null,
    itemSearch: ''
  });

  // Track applied filters separately to avoid re-fetching on every filter change
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(filters);

  // Extract unique merchants from receipts for the merchant filter
  const availableMerchants = useMemo(() => {
    const merchants = new Set(receipts.map(r => r.merchant));
    return Array.from(merchants).sort();
  }, [receipts]);

  // Override AppShell's max-width constraint for mobile, keep constrained on desktop
  useEffect(() => {
    const main = document.querySelector('main');
    if (main instanceof HTMLElement) {
      // Only go full-width on mobile (< 768px)
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        main.style.width = '100%';
        main.style.maxWidth = '100%';
        main.style.padding = '0';
      } else {
        // Desktop: keep constrained but wider
        main.style.maxWidth = '900px';
        main.style.margin = '0 auto';
      }
    }
    return () => {
      if (main instanceof HTMLElement) {
        main.style.width = '';
        main.style.maxWidth = '';
        main.style.padding = '';
        main.style.margin = '';
      }
    };
  }, []);

  useEffect(() => {
    loadReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReceipts = async (offset: number = 0, append: boolean = false, filterOverride?: FilterState) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setError(null); // Clear previous errors on new load
    }

    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setError('Nepodarilo sa získať prihlásenie');
        setIsLoading(false);
        setIsLoadingMore(false);
        return;
      }

      // Build query parameters with filters
      const params = new URLSearchParams();
      params.append('limit', '30');
      params.append('offset', offset.toString());

      const activeFilters = filterOverride || appliedFilters;

      // Date filters
      if (activeFilters.dateFrom) {
        params.append('dateFrom', activeFilters.dateFrom.toISOString().split('T')[0]);
      }
      if (activeFilters.dateTo) {
        params.append('dateTo', activeFilters.dateTo.toISOString().split('T')[0]);
      }

      // Amount filters
      if (activeFilters.amountMin) {
        params.append('minAmount', activeFilters.amountMin);
      }
      if (activeFilters.amountMax) {
        params.append('maxAmount', activeFilters.amountMax);
      }

      // Split status filter
      if (activeFilters.splitStatus !== 'all') {
        params.append('hasSplit', activeFilters.splitStatus === 'split' ? 'true' : 'false');
      }

      // Merchant filter
      if (activeFilters.selectedMerchants.length > 0) {
        activeFilters.selectedMerchants.forEach(m => params.append('merchants', m));
      }

      // Category filter
      if (activeFilters.selectedCategory) {
        params.append('category', activeFilters.selectedCategory);
      }

      // Item search
      if (activeFilters.itemSearch) {
        params.append('itemSearch', activeFilters.itemSearch);
      }

      const response = await fetchWithRetry(`${config.apiUrl}/getReceipts?${params.toString()}`, {
        headers: {
          'X-Auth-Token': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        setError(`Chyba pri načítaní: ${response.status}`);
        setIsLoading(false);
        setIsLoadingMore(false);
        return;
      }

      const data = await response.json();
      setError(null); // Clear error on success

      if (append) {
        setReceipts(prev => [...prev, ...data.receipts]);
      } else {
        setReceipts(data.receipts);

        // Track bill list view (only on initial load, not pagination)
        trackEvent('bill_list_viewed', {
          bills_shown: data.receipts.length,
          total_bills: data.pagination.total,
          has_more: data.pagination.hasMore,
          filters_active: Object.keys(activeFilters).some(k => {
            const val = activeFilters[k as keyof FilterState];
            return val !== null && val !== '' && val !== 'all' && (!Array.isArray(val) || val.length > 0);
          })
        });
      }
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to load receipts:', error);
      setError('Nepodarilo sa načítať bločky. Skúste to znovu.');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (pagination && pagination.hasMore) {
      loadReceipts(pagination.offset + pagination.limit, true);
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setShowSearch(false); // Collapse filter panel after applying
    loadReceipts(0, false, filters); // Reset to first page with new filters
    trackEvent('filters_applied', {
      date_filter: filters.datePreset || 'none',
      amount_filter: filters.amountPreset || 'none',
      split_filter: filters.splitStatus,
      merchants_count: filters.selectedMerchants.length,
      has_category: !!filters.selectedCategory,
      has_item_search: !!filters.itemSearch
    });
  };

  const handleClearFilters = () => {
    const clearedFilters: FilterState = {
      datePreset: null,
      dateFrom: null,
      dateTo: null,
      amountPreset: null,
      amountMin: '',
      amountMax: '',
      splitStatus: 'all',
      selectedMerchants: [],
      merchantSearch: '',
      selectedCategory: null,
      itemSearch: ''
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    loadReceipts(0, false, clearedFilters);
    trackEvent('filters_cleared');
  };

  const formatDateTime = (datetime: string) => {
    // Parse the datetime string directly without timezone conversion
    const match = datetime.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hours, minutes] = match;
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    }
    // Fallback if format is unexpected
    const date = new Date(datetime);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols: { [key: string]: string } = {
      'EUR': '€',
      'CZK': 'Kč',
      'PLN': 'zł',
      'USD': '$',
      'GBP': '£'
    };
    return symbols[currency] || currency;
  };

  const truncateMerchant = (merchant: string, maxLength: number = 40) => {
    if (merchant.length <= maxLength) return merchant;
    return merchant.substring(0, maxLength) + '...';
  };

  const getInitial = (merchant: string) => {
    return merchant.charAt(0).toUpperCase();
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div style={{ padding: '20px 20px 100px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', margin: 0 }}>
            Vaše bločky
          </h1>
          <div style={{ width: '40px', height: '40px', background: '#e5e7eb', borderRadius: '50%' }} />
        </div>

        {/* Skeleton receipts */}
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{ width: '44px', height: '44px', background: '#e5e7eb', borderRadius: '50%', flexShrink: 0, animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={{ flex: 1 }}>
              <div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '70%', marginBottom: '6px', animation: 'pulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: '12px', background: '#e5e7eb', borderRadius: '4px', width: '40%', animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
            <div style={{ height: '18px', background: '#e5e7eb', borderRadius: '4px', width: '60px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
        ))}

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '20px 20px 100px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', margin: 0 }}>
            Vaše bločky
          </h1>
          <button
            onClick={() => setShowSearch(!showSearch)}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '1px solid #e5e7eb',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}
          >
            🔍
          </button>
        </div>

        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '40px 20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Nepodarilo sa načítať bločky
          </h2>
          <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>
            {error}
          </p>
          <button
            onClick={() => loadReceipts()}
            style={{
              background: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            🔄 Obnoviť
          </button>
        </div>
      </div>
    );
  }

  // Check if any filters are active
  const hasActiveFilters =
    appliedFilters.datePreset ||
    appliedFilters.amountPreset ||
    appliedFilters.splitStatus !== 'all' ||
    appliedFilters.selectedMerchants.length > 0 ||
    appliedFilters.selectedCategory ||
    appliedFilters.itemSearch;

  // Empty state
  if (receipts.length === 0 && !isLoading) {
    return (
      <div style={{ padding: '20px 20px 100px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', margin: 0 }}>
            Vaše bločky
          </h1>
          <button
            onClick={() => setShowSearch(!showSearch)}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '1px solid #e5e7eb',
              background: showSearch ? '#667eea' : '#fff',
              color: showSearch ? '#fff' : '#000',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              transition: 'all 0.2s'
            }}
          >
            🔍
          </button>
        </div>

        {/* Active filters bar (when collapsed) - SHOW EVEN ON EMPTY STATE */}
        {!showSearch && hasActiveFilters && (
          <div
            onClick={() => setShowSearch(true)}
            style={{
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '20px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#667eea';
              e.currentTarget.style.background = '#faf5ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.background = '#f3f4f6';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#667eea' }}>
                🔍 Filtre aktívne
              </span>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                Klikni pre úpravu
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {appliedFilters.datePreset && (
                <span style={{
                  background: '#ede9fe',
                  color: '#6b21a8',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  📅 {appliedFilters.datePreset === 'week' ? 'Tento týždeň' :
                       appliedFilters.datePreset === 'month' ? 'Tento mesiac' :
                       appliedFilters.datePreset === 'lastMonth' ? 'Minulý mesiac' :
                       appliedFilters.datePreset === 'twoYearsAgo' ? 'Spred 2 rokov' : 'Vlastné'}
                </span>
              )}
              {appliedFilters.selectedMerchants.length > 0 && (
                <span style={{
                  background: '#dbeafe',
                  color: '#1e40af',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  🏪 {appliedFilters.selectedMerchants.length} {appliedFilters.selectedMerchants.length === 1 ? 'obchod' : appliedFilters.selectedMerchants.length < 5 ? 'obchody' : 'obchodov'}
                </span>
              )}
              {appliedFilters.amountPreset && (
                <span style={{
                  background: '#d1fae5',
                  color: '#065f46',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  💰 {appliedFilters.amountPreset === 'custom'
                    ? `${appliedFilters.amountMin || '0'}-${appliedFilters.amountMax || '∞'}`
                    : appliedFilters.amountPreset}
                </span>
              )}
              {appliedFilters.splitStatus !== 'all' && (
                <span style={{
                  background: '#fef3c7',
                  color: '#92400e',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  👥 {appliedFilters.splitStatus === 'split' ? 'Rozdelené' : 'Nerozdelené'}
                </span>
              )}
              {appliedFilters.selectedCategory && (
                <span style={{
                  background: '#fee2e2',
                  color: '#991b1b',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  🏷️ Kategória
                </span>
              )}
              {appliedFilters.itemSearch && (
                <span style={{
                  background: '#e0e7ff',
                  color: '#3730a3',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500
                }}>
                  🔍 "{appliedFilters.itemSearch.length > 15 ? appliedFilters.itemSearch.substring(0, 15) + '...' : appliedFilters.itemSearch}"
                </span>
              )}
            </div>
          </div>
        )}

        {/* Search filter panel (when expanded) */}
        {showSearch && (
          <SearchFilterPanel
            filters={filters}
            onFiltersChange={setFilters}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            availableMerchants={availableMerchants}
            isLoading={isLoading}
          />
        )}

        {/* Empty state content - different messages for filtered vs unfiltered */}
        {hasActiveFilters ? (
          // Filtered but no results
          <div style={{
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: '12px',
            padding: '40px 20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
              Nenašli sa žiadne bločky
            </h2>
            <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '16px' }}>
              Pre zvolené filtre nemáme žiadne výsledky
            </p>

            {/* Tips section */}
            <div style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <p style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                💡 Tipy:
              </p>
              <ul style={{ fontSize: '13px', color: '#6b7280', margin: 0, paddingLeft: '20px', lineHeight: '1.6' }}>
                <li style={{ marginBottom: '6px' }}>Skúste zmeniť alebo odstrániť niektoré filtre</li>
                <li style={{ marginBottom: '6px' }}>Rozšírte časové obdobie vyhľadávania</li>
                {appliedFilters.itemSearch && (
                  <li style={{ marginBottom: '6px' }}>
                    <strong>Hľadáte položku?</strong> Mnohé obchody používajú skratky na bločkoch
                    (napr. "Chlieb" môže byť len "Chl.", "Mlieko" ako "Ml."). Skúste kratší výraz.
                  </li>
                )}
                {appliedFilters.selectedMerchants.length > 0 && (
                  <li style={{ marginBottom: '6px' }}>Skúste vybrať iné obchody alebo zrušte filter obchodov</li>
                )}
              </ul>
            </div>

            <button
              onClick={handleClearFilters}
              style={{
                background: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Vymazať všetky filtre
            </button>
          </div>
        ) : (
          // No receipts at all
          <div style={{
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '40px 20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
              Zatiaľ nemáte žiadne bločky
            </h2>
            <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>
              Pridajte svoj prvý bloček cez tlačidlo Pridať
            </p>
            <button
              onClick={() => navigate('/add')}
              style={{
                background: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Pridať bloček
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 20px 100px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', margin: 0 }}>
          Vaše bločky
        </h1>
        <button
          onClick={() => setShowSearch(!showSearch)}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: '1px solid #e5e7eb',
            background: showSearch ? '#667eea' : '#fff',
            color: showSearch ? '#fff' : '#000',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            transition: 'all 0.2s'
          }}
        >
          🔍
        </button>
      </div>

      {/* Active filters bar (when collapsed) */}
      {!showSearch && (
        appliedFilters.datePreset ||
        appliedFilters.amountPreset ||
        appliedFilters.splitStatus !== 'all' ||
        appliedFilters.selectedMerchants.length > 0 ||
        appliedFilters.selectedCategory ||
        appliedFilters.itemSearch
      ) && (
        <div
          onClick={() => setShowSearch(true)}
          style={{
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '20px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#667eea';
            e.currentTarget.style.background = '#faf5ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.background = '#f3f4f6';
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#667eea' }}>
              🔍 Filtre aktívne
            </span>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
              Klikni pre úpravu
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {appliedFilters.datePreset && (
              <span style={{
                background: '#ede9fe',
                color: '#6b21a8',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500
              }}>
                📅 {appliedFilters.datePreset === 'week' ? 'Tento týždeň' :
                     appliedFilters.datePreset === 'month' ? 'Tento mesiac' :
                     appliedFilters.datePreset === 'lastMonth' ? 'Minulý mesiac' :
                     appliedFilters.datePreset === 'twoYearsAgo' ? 'Záruka' : 'Vlastné'}
              </span>
            )}
            {appliedFilters.selectedMerchants.length > 0 && (
              <span style={{
                background: '#dbeafe',
                color: '#1e40af',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500
              }}>
                🏪 {appliedFilters.selectedMerchants.length} {appliedFilters.selectedMerchants.length === 1 ? 'obchod' : appliedFilters.selectedMerchants.length < 5 ? 'obchody' : 'obchodov'}
              </span>
            )}
            {appliedFilters.amountPreset && (
              <span style={{
                background: '#d1fae5',
                color: '#065f46',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500
              }}>
                💰 {appliedFilters.amountPreset === 'custom'
                  ? `${appliedFilters.amountMin || '0'}-${appliedFilters.amountMax || '∞'}`
                  : appliedFilters.amountPreset}
              </span>
            )}
            {appliedFilters.splitStatus !== 'all' && (
              <span style={{
                background: '#fef3c7',
                color: '#92400e',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500
              }}>
                👥 {appliedFilters.splitStatus === 'split' ? 'Rozdelené' : 'Nerozdelené'}
              </span>
            )}
            {appliedFilters.selectedCategory && (
              <span style={{
                background: '#fee2e2',
                color: '#991b1b',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500
              }}>
                🏷️ Kategória
              </span>
            )}
            {appliedFilters.itemSearch && (
              <span style={{
                background: '#e0e7ff',
                color: '#3730a3',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500
              }}>
                🔍 "{appliedFilters.itemSearch.length > 15 ? appliedFilters.itemSearch.substring(0, 15) + '...' : appliedFilters.itemSearch}"
              </span>
            )}
          </div>
        </div>
      )}

      {/* Search filter panel (when expanded) */}
      {showSearch && (
        <SearchFilterPanel
          filters={filters}
          onFiltersChange={setFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          availableMerchants={availableMerchants}
          isLoading={isLoading}
        />
      )}

      {/* Receipts list */}
      <div>
        {receipts.map((receipt) => (
          <div
            key={receipt.receipt_id}
            onClick={() => navigate(`/receipts/${receipt.receipt_id}`)}
            style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#667eea';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {/* Merchant Logo */}
            <MerchantLogo
              merchant={receipt.merchant}
              logo={receipt.logo}
              size={44}
            />

            {/* Merchant & DateTime */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#111827',
                marginBottom: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {truncateMerchant(receipt.merchant)}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {formatDateTime(receipt.datetime)}
              </div>
            </div>

            {/* Amount */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              marginRight: '8px',
              flexShrink: 0,
              gap: '2px'
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: 700,
                color: '#667eea'
              }}>
                {/* Show user's share if split and we have it, otherwise show total */}
                {receipt.has_split && receipt.user_share_amount != null
                  ? receipt.user_share_amount.toFixed(2)
                  : receipt.total_gross.toFixed(2)
                }{getCurrencySymbol(receipt.currency)}
              </div>
              {receipt.has_split && receipt.user_share_amount != null && (
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  whiteSpace: 'nowrap'
                }}>
                  👥 Tvoja časť
                </div>
              )}
              {receipt.has_split && receipt.user_share_amount == null && (
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  whiteSpace: 'nowrap'
                }}>
                  👥
                </div>
              )}
            </div>

            {/* Arrow */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}>
              <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        ))}
      </div>

      {/* Load More button */}
      {pagination && pagination.hasMore && (
        <button
          onClick={handleLoadMore}
          disabled={isLoadingMore}
          style={{
            width: '100%',
            background: '#fff',
            color: '#667eea',
            border: '1px solid #667eea',
            borderRadius: '12px',
            padding: '14px 24px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: isLoadingMore ? 'not-allowed' : 'pointer',
            marginTop: '8px',
            opacity: isLoadingMore ? 0.6 : 1
          }}
        >
          {isLoadingMore ? 'Načítavam...' : `Načítať viac (${pagination.total - receipts.length} zostáva)`}
        </button>
      )}

      {/* Total count */}
      {pagination && (
        <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '16px' }}>
          Zobrazené {receipts.length} z {pagination.total} bločkov
        </p>
      )}
    </div>
  );
}
